import { useState } from "react";
import type { TrainingPlan, PlannedSession, GoalMetric } from "@runlens/shared";
import {
  CalendarDaysIcon,
  FlagIcon,
  FireIcon,
  ArrowPathIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { GoalMetrics } from "./GoalMetrics";

interface PlanHeaderProps {
  plan: TrainingPlan;
  sessions: PlannedSession[];
  metrics?: GoalMetric[];
  onReset?: () => Promise<void>;
  onDelete?: () => Promise<void>;
}

const goalEventLabels: Record<string, string> = {
  marathon: "Marathon",
  half: "Half Marathon",
  "10k": "10K",
  "5k": "5K",
  custom: "Custom Goal",
};

const goalTypeLabels: Record<string, string> = {
  race: "Race Preparation",
  fitness: "General Fitness",
  habit: "Build Consistency",
  custom: "Custom Plan",
};

function formatDistance(meters: number): string {
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(1)}km`;
  }
  return `${meters}m`;
}

function getWeekProgress(plan: TrainingPlan): { current: number; total: number } | null {
  if (!plan.goalDate) return null;

  const now = new Date();
  const start = new Date(plan.createdAt);
  const end = new Date(plan.goalDate);

  const totalWeeks = Math.ceil(
    (end.getTime() - start.getTime()) / (7 * 24 * 60 * 60 * 1000)
  );
  const currentWeek = Math.ceil(
    (now.getTime() - start.getTime()) / (7 * 24 * 60 * 60 * 1000)
  );

  return {
    current: Math.max(1, Math.min(currentWeek, totalWeeks)),
    total: totalWeeks,
  };
}

function getThisWeekStats(sessions: PlannedSession[]): {
  totalDistance: number;
  sessionCount: number;
} {
  const now = new Date();
  const currentDay = now.getDay();
  const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() + mondayOffset);
  weekStart.setHours(0, 0, 0, 0);

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);

  const thisWeekSessions = sessions.filter((s) => {
    const date = new Date(s.scheduledDate);
    return date >= weekStart && date <= weekEnd;
  });

  return {
    totalDistance: thisWeekSessions.reduce(
      (sum, s) => sum + (s.targetDistance || 0),
      0
    ),
    sessionCount: thisWeekSessions.filter((s) => s.sessionType !== "rest").length,
  };
}

export function PlanHeader({ plan, sessions, metrics, onReset, onDelete }: PlanHeaderProps) {
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const weekProgress = getWeekProgress(plan);
  const thisWeek = getThisWeekStats(sessions);

  const handleReset = async () => {
    if (!onReset) return;
    setIsResetting(true);
    try {
      await onReset();
      setShowResetConfirm(false);
    } finally {
      setIsResetting(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setIsDeleting(true);
    try {
      await onDelete();
      setShowDeleteConfirm(false);
    } catch {
      setIsDeleting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 h-full flex flex-col">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-lg font-semibold text-gray-900">{plan.name}</h2>
          {onReset && (
            <button
              type="button"
              onClick={() => setShowResetConfirm(true)}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
              title="Reset plan"
            >
              <ArrowPathIcon className="w-4 h-4" />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
              title="Delete plan"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Goal info - stacked vertically for narrow width */}
      <div className="flex flex-col gap-1 mt-3 text-sm text-gray-500">
        {plan.goalType && (
          <span className="flex items-center gap-1">
            <FlagIcon className="w-4 h-4" />
            {goalTypeLabels[plan.goalType] || plan.goalType}
          </span>
        )}
        {plan.goalEvent && (
          <span className="flex items-center gap-1">
            <FireIcon className="w-4 h-4" />
            {goalEventLabels[plan.goalEvent] || plan.goalEvent}
          </span>
        )}
        {plan.goalDate && (
          <span className="flex items-center gap-1">
            <CalendarDaysIcon className="w-4 h-4" />
            {new Date(plan.goalDate).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </span>
        )}
      </div>

      {/* Goal Metrics */}
      {metrics && metrics.length > 0 && (
        <div className="mt-3">
          <GoalMetrics metrics={metrics} />
        </div>
      )}

      {/* Week progress */}
      {weekProgress && (
        <div className="mt-3">
          <div className="text-sm font-medium text-gray-900">
            Week {weekProgress.current} of {weekProgress.total}
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full mt-1 overflow-hidden">
            <div
              className="h-full bg-brand-500 rounded-full transition-all"
              style={{
                width: `${(weekProgress.current / weekProgress.total) * 100}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* This week summary */}
      <div className="mt-auto pt-3 border-t border-gray-100">
        <span className="text-xs text-gray-500">This Week</span>
        <p className="text-sm font-medium text-gray-900">
          {thisWeek.sessionCount} session{thisWeek.sessionCount !== 1 ? "s" : ""}
          {thisWeek.totalDistance > 0 && (
            <span className="text-gray-500">
              {" "}
              · {formatDistance(thisWeek.totalDistance)}
            </span>
          )}
        </p>

        {plan.status !== "active" && (
          <span
            className={`mt-2 inline-block px-2 py-0.5 text-xs font-medium rounded-full ${
              plan.status === "completed"
                ? "bg-green-100 text-green-700"
                : plan.status === "paused"
                  ? "bg-yellow-100 text-yellow-700"
                  : "bg-gray-100 text-gray-700"
            }`}
          >
            {plan.status.charAt(0).toUpperCase() + plan.status.slice(1)}
          </span>
        )}
      </div>

      {/* Reset confirmation dialog */}
      {showResetConfirm && (
        <div className="mt-4 pt-3 border-t border-gray-100">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-800">
              This will delete all scheduled sessions and chat history. Are you sure?
            </p>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={handleReset}
                disabled={isResetting}
                className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50 transition-colors"
              >
                {isResetting ? "Resetting..." : "Yes, Reset Plan"}
              </button>
              <button
                type="button"
                onClick={() => setShowResetConfirm(false)}
                disabled={isResetting}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation dialog */}
      {showDeleteConfirm && (
        <div className="mt-4 pt-3 border-t border-gray-100">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-800">
              This will permanently delete this plan and all its data. This cannot be undone.
            </p>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50 transition-colors"
              >
                {isDeleting ? "Deleting..." : "Yes, Delete Plan"}
              </button>
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
